import React from 'react';
import { Box, Typography, Card, CardContent, CardMedia, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useContinents } from '../hooks/useMarketData';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { createSlug } from '../utils/slug';

export const Continents: React.FC = () => {
  usePageTitle('Browse by Continent');
  const navigate = useNavigate();
  const { data, isLoading, error } = useContinents();

  if (isLoading) {
    return <LoadingSpinner message="Loading continents..." />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load continents" />;
  }

  const continents = data?.continents || [];

  const getContinentImagePath = (continentName: string): string => {
    const imageName = createSlug(continentName);
    return `/images/continents/${imageName}.png`;
  };

  return (
    <Box pb={10}>
      <Box px={2} pt={2} mb={3}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Browse by Continent
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a continent to view countries and markets
        </Typography>
      </Box>

      <Box px={2}>
        <Grid container spacing={2}>
          {continents.map((continent) => (
            <Grid item xs={12} key={continent.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  },
                }}
                onClick={() => navigate(`/countries/${continent.id}`)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CardMedia
                    component="img"
                    sx={{
                      width: 100,
                      height: 100,
                      objectFit: 'cover',
                      backgroundColor: '#f5f5f5'
                    }}
                    image={getContinentImagePath(continent.name)}
                    alt={continent.name}
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {continent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {continent.total_markets || 0} markets • {continent.total_countries || 0} countries
                    </Typography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};